"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Story() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const milestones = [{
    date: "2018 · Q1",
    title: "KTEX platform foundation",
    description: "Established the core digital marketplace infrastructure to bridge the gap between global textile suppliers and buyers.",
    status: "SHIPPED"
  }, {
    date: "2019 · Q3",
    title: "Verified supplier network",
    description: "Launched our rigorous vetting process to ensure quality and reliability across all listed textile manufacturers.",
    status: "SHIPPED"
  }, {
    date: "2021 · Q2",
    title: "Automated trade matching",
    description: "Introduced AI-driven request matching to connect buyers with the most relevant suppliers in real-time.",
    status: "SHIPPED"
  }, {
    date: "2023 · Q4",
    title: "Global logistics integration",
    description: "Partnering with international freight leaders to provide seamless door-to-door shipping solutions for our members.",
    status: "IN FLIGHT"
  }, {
    date: "2024 · Q2",
    title: "Sustainable sourcing portal",
    description: "Dedicated hub for eco-friendly and certified organic textile materials to support green supply chains.",
    status: "PLANNED"
  }, {
    date: "2025 · Q1",
    title: "Real-time trade analytics",
    description: "Advanced dashboard providing market trends, pricing insights, and demand forecasting for our premium members.",
    status: "PLANNED"
  }, {
    date: "2026 · Q1",
    title: "Blockchain trade security",
    description: "Implementing immutable ledger technology to guarantee transparent and secure payment processing for all transactions.",
    status: "PLANNED"
  }];
  return <section data-section-id="19921" id="our-story" className="bg-white text-[#1a1a1a] py-24 md:py-32 border-t" style={{
    borderColor: "#e0e0e0"
  }}>
      <div className="max-w-[1320px] mx-auto px-5 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          {}
          <div className="lg:col-span-4">
            <div ref={headerRef} className={`lg:sticky lg:top-24 transition-all duration-500 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
              <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#1a3a52] mb-8" style={{
              lineHeight: 1
            }}>
                {"OUR JOURNEY · 2018 → 2026"}
              </p>
              <h2 className="font-default font-semibold text-[#1a1a1a] mb-6" style={{
              fontSize: "clamp(2rem, 3.6vw, 3.25rem)",
              letterSpacing: "-0.035em",
              lineHeight: 1.02
            }}>
                Building the future of textile trade.
              </h2>
              <p className="font-default text-[#1a1a1a]/80" style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
              letterSpacing: "-0.005em",
              lineHeight: 1.55
            }}>
                A commitment to transparency and efficiency. Every milestone we reach is designed to simplify global sourcing and empower businesses to grow with confidence.
              </p>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <div className="relative">
              {}
              <div className="absolute left-[3px] top-2 bottom-2 w-px" style={{
              background: "#e0e0e0"
            }} aria-hidden="true" />

              <ol className="space-y-12 md:space-y-14">
                {milestones.map((m, i) => {
                const isCurrent = m.status === "IN FLIGHT";
                const isShipped = m.status === "SHIPPED";
                return <Milestone key={i} index={i} date={m.date} title={m.title} description={m.description} status={m.status} isCurrent={isCurrent} isShipped={isShipped} />;
              })}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function Milestone({
  index,
  date,
  title,
  description,
  status,
  isCurrent,
  isShipped
}: {
  index: number;
  date: string;
  title: string;
  description: string;
  status: string;
  isCurrent: boolean;
  isShipped: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>({
    threshold: 0.15
  });
  return <li ref={ref} data-index={index} className="relative pl-10 md:pl-14 transition-all ease-out" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(8px)",
    transitionDuration: "480ms",
    transitionDelay: `${index * 80}ms`
  }}>
      {}
      <span className="absolute left-0 top-1.5 w-2 h-2 border" style={{
      borderColor: isCurrent ? "#1a3a52" : "#e0e0e0",
      background: isCurrent ? "#1a3a52" : "transparent"
    }} aria-hidden="true" />

      {}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#1a3a52]" style={{
        lineHeight: 1
      }}>
          {date}
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] px-2 py-1 border" style={{
        lineHeight: 1,
        color: isCurrent ? "#1a3a52" : "#666666",
        borderColor: isCurrent ? "#1a3a52" : "#e0e0e0",
        opacity: isCurrent ? 1 : 0.9
      }}>
          {"[" + status + "]"}
        </span>
      </div>

      {}
      <h3 className="font-default font-medium text-[#1a1a1a] mb-2" style={{
      fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)",
      letterSpacing: "-0.02em",
      lineHeight: 1.15
    }}>
        {title}
      </h3>

      {}
      <p className="font-default text-[#1a1a1a]/70 max-w-[560px]" style={{
      fontSize: "0.9375rem",
      lineHeight: 1.6
    }}>
        {description}
      </p>
    </li>;
}